/**
 * useKeyInspector
 *
 * In-context translation key inspector: while active, hovering any
 * translated text outlines it and shows a small icon; clicking that icon
 * reveals the i18next key that produced the text, instead of an admin
 * having to guess a key from its rendered English text on the Localization
 * page's search box. Alt+click is a keyboard-friendly shortcut that skips
 * the hover step (see below for how its matching differs).
 *
 * Unlike the first version of this tool, inspect mode no longer intercepts
 * plain clicks — the whole point of moving to a hover-driven icon is that
 * the rest of the app (dialogs, dropdowns, tabs, navigation) keeps working
 * normally while inspecting, rather than every click being hijacked.
 *
 * Follows the exact same access rule as the Localization page and the
 * onboarding localization wizard step — the `SETTINGS` entitlement — rather
 * than a separate role check. By default that's TECHNICAL_ADMIN,
 * CLINICAL_ADMIN and ENTERPRISE_ADMIN, plus whatever a hospital has granted
 * SETTINGS on its own Roles & Permissions page; this composable doesn't
 * hardcode any of that, it just asks the entitlements store.
 *
 * Two independent paths resolve to a key:
 *  - Hover: {@link findMarkerSourceForHover} walks from the hovered element
 *    up through its ancestors (bounded by {@link MAX_WALK_DEPTH}), checking
 *    `title`/`placeholder`/`value` first, then each node's own *direct* text
 *    (not its full subtree), for an invisible key marker (see
 *    `shared/utils/key-markers.ts`). `aria-label` is deliberately not
 *    checked — it never renders anything a sighted user can see, so an
 *    icon-only button whose only marked string is its aria-label has no
 *    visible text to inspect, and shouldn't outline/highlight as if it did.
 *    Direct text only — rather than
 *    `textContent`, which includes descendants — matters here in a way it
 *    didn't for the old click-only version: hovering is continuous, so
 *    matching on the full subtree would outline an entire card any time the
 *    pointer crossed empty space inside it, as long as something inside it
 *    (e.g. its heading) happened to be marked.
 *  - Alt+click: {@link findMarkerSource} does the same walk but against full
 *    `textContent`, and if nothing is marked, falls back to a reverse
 *    lookup — searching the loaded bundle for a template whose `{{vars}}`
 *    pattern matches the clicked text. Ambiguous by nature (many keys can
 *    share a template like "Save"), so this returns every candidate rather
 *    than guessing one. This path exists for text that isn't marked at all
 *    (not routed through `t()`, or inspect mode was switched on after that
 *    binding last rendered) and for cases the hover walk's stricter
 *    matching misses.
 *  - Neither finds anything → the popover reports "not translatable" (only
 *    reachable via Alt+click — the hover icon never appears for unmarked
 *    text in the first place).
 *
 * The hover/click listeners and the marker post-processor are all switched
 * off the instant the `SETTINGS` entitlement is lost (role change, logout) —
 * see the `watch` in {@link useKeyInspector} — so there's no window where
 * markers keep being minted after access is revoked.
 */
import type { Ref } from 'vue';
import { useAuthStore, useEntitlementsStore } from '@/stores';
import {
  containsKeyMarker,
  decodeKeyMarkers,
  stripKeyMarkers,
  setKeyMarkersEnabled,
  type DecodedKeyMarker,
} from '@/shared/utils/key-markers';
import { i18next, DEFAULT_NAMESPACE } from '@/core/plugins/i18n';

// ── Types ────────────────────────────────────────────────────────────────────

export type KeyInspectorSelection = {
  /** The key found, or `null` when the clicked text isn't translatable. */
  key: string | null;
  /** Namespace the key was resolved in, if known. */
  ns?: string;
  /** The visible text that was clicked, with any markers stripped. */
  matchedText: string;
  /** Raw i18next template for `key` (before interpolation), if known. */
  template?: string;
  /** Other keys found in the same text — additional markers, or additional reverse-lookup candidates. */
  otherMatches: DecodedKeyMarker[];
  /** How `key` was determined. */
  resolvedVia: 'marker' | 'fallback' | 'none';
  /** Viewport coordinates of the click, for anchoring the popover next to it. */
  position: { x: number; y: number };
};

// ── Module-level singleton state ──────────────────────────────────────────────
//
// Shared across every call to useKeyInspector(), the same pattern
// useMrnLabel/usePatientLabel use — inspect mode is a single, app-wide
// on/off switch, not something that should have one copy per component.

const _isActive = ref(false);
const _selected = ref<KeyInspectorSelection | null>(null);

/** Guards the one-time watcher/listener setup below against being installed twice. */
let _initialized = false;

// ── DOM walk ──────────────────────────────────────────────────────────────────

/** How far up the ancestor chain to look for a marker before giving up. */
const MAX_WALK_DEPTH = 8;

/**
 * Checked in this order, before falling back to `textContent`.
 * Deliberately excludes `aria-label` — it's never visually rendered, so a
 * marker living only there means there's no visible text at this spot to
 * inspect (e.g. an icon-only button with no text, title or placeholder).
 * Highlighting/resolving a key purely from an invisible attribute would
 * make icon-only controls light up with nothing on screen to justify it.
 */
const INSPECT_ATTRS = ['title', 'placeholder', 'value'] as const;

function findMarkerSource(start: Element | null): { text: string; markers: DecodedKeyMarker[] } | null {
  let node: Element | null = start;
  let depth = 0;
  while (node && depth < MAX_WALK_DEPTH) {
    for (const attr of INSPECT_ATTRS) {
      const value = node.getAttribute(attr);
      if (value && containsKeyMarker(value)) {
        return { text: value, markers: decodeKeyMarkers(value) };
      }
    }
    const text = node.textContent ?? '';
    if (containsKeyMarker(text)) {
      return { text, markers: decodeKeyMarkers(text) };
    }
    node = node.parentElement;
    depth += 1;
  }
  return null;
}

// ── Reverse lookup (fallback for unmarked text) ──────────────────────────────

const TEMPLATE_VAR_RE = /\{\{[^}]+\}\}/g;
const REGEX_SPECIAL_RE = /[.*+?^${}()|[\]\\]/g;

/**
 * Turn an i18next template into a regex that matches its *resolved* form:
 * literal segments are escaped, and each `{{var}}` becomes a non-greedy
 * wildcard. A template with no variables becomes an exact-match regex —
 * a plain string equality would work too, but reusing one code path for
 * both cases keeps {@link reverseLookupKeys} simple.
 */
function templateToRegex(template: string): RegExp {
  const parts = template
    .split(TEMPLATE_VAR_RE)
    .map((part) => part.replace(REGEX_SPECIAL_RE, '\\$&'));
  return new RegExp(`^${parts.join('.*?')}$`, 's');
}

/**
 * Search the loaded bundle for every key whose template could resolve to
 * `text`. Deliberately returns every match rather than the first — a
 * one-word template like "Save" or a bare `{{patientLabel}}" matches many
 * keys, and guessing wrong would send an admin to edit the wrong string.
 *
 * O(bundle size) per call: this walks every key in the active namespace on
 * every click that doesn't resolve via a marker. Acceptable for an
 * admin-only, click-at-a-time tool over a bundle of a few thousand keys;
 * not something to run in a hot path.
 */
function reverseLookupKeys(text: string): string[] {
  const bundle = i18next.getResourceBundle(i18next.language, DEFAULT_NAMESPACE) as
    | Record<string, string>
    | null;
  if (!bundle) return [];

  const matches: string[] = [];
  for (const [key, template] of Object.entries(bundle)) {
    if (typeof template !== 'string' || !template) continue;
    if (templateToRegex(template).test(text)) matches.push(key);
  }
  return matches;
}

function lookupTemplate(key: string, ns: string | undefined): string | undefined {
  const bundle = i18next.getResourceBundle(i18next.language, ns ?? DEFAULT_NAMESPACE) as
    | Record<string, string>
    | null;
  return bundle?.[key];
}

/**
 * Builds a 'marker' selection — shared by Alt+click and the hover icon's own
 * click. The *last* marker in the string is treated as primary, not the
 * first: when a translation is composed — `t(key, { someVar: t(innerKey) })`
 * — the inner call's marker ends up embedded earlier in the string (it was
 * substituted in during interpolation), while the outer call's own marker is
 * always appended last (see keyMarkerPostProcessor). The outer key is the
 * one that actually produced *this* text (e.g. a card title built by
 * interpolating a separately-translated section name into it); the inner
 * one is just an ingredient it happened to interpolate, so it belongs in
 * otherMatches, not as the primary result.
 */
function selectionFromMarkerMatch(
  found: { text: string; markers: DecodedKeyMarker[] },
  position: { x: number; y: number },
): KeyInspectorSelection {
  const primary = found.markers[found.markers.length - 1];
  const rest = found.markers.slice(0, -1);
  return {
    key: primary.key,
    ns: primary.ns,
    matchedText: stripKeyMarkers(found.text),
    template: lookupTemplate(primary.key, primary.ns),
    otherMatches: rest,
    resolvedVia: 'marker',
    position,
  };
}

// ── Hover tracking ────────────────────────────────────────────────────────────

export type KeyInspectorHoverTarget = {
  /**
   * The element whose own attribute/text actually carried the marker — not
   * necessarily the element the pointer is directly over, since this is
   * found by walking upward (see {@link findMarkerSourceForHover}). The
   * outline and icon in {@link KeyInspectorOverlay} are anchored to this
   * element's rect, not the raw pointer position.
   */
  element: Element;
  text: string;
  markers: DecodedKeyMarker[];
  /** Viewport rect of `element`, re-measured on scroll/resize and when a transition/animation ends — see {@link remeasureHovered}. */
  rect: DOMRect;
};

const _hovered = ref<KeyInspectorHoverTarget | null>(null);

/**
 * Grace period before clearing the hover once the pointer leaves marked
 * content, so it can cross the small visual gap onto the hover icon itself
 * without the icon disappearing out from under it first.
 */
const HOVER_CLEAR_DELAY_MS = 150;
let hoverClearTimer: ReturnType<typeof setTimeout> | undefined;

/** requestAnimationFrame handle for the pending hover-matching pass; batches rapid `mouseover` churn to once per frame. */
let hoverFrame: ReturnType<typeof requestAnimationFrame> | undefined;
let pendingHoverTarget: Element | null = null;

/** Concatenates only `node`'s direct text-node children — not its full subtree, unlike `node.textContent`. */
function ownText(node: Element): string {
  let text = '';
  for (const child of node.childNodes) {
    if (child.nodeType === Node.TEXT_NODE) text += child.textContent ?? '';
  }
  return text;
}

/**
 * Same ancestor walk as {@link findMarkerSource}, but matches each node's own
 * direct text rather than its full `textContent` — see the module docblock
 * for why that distinction matters for a continuous hover, not just a
 * one-off click.
 */
function findMarkerSourceForHover(
  start: Element | null,
): { element: Element; text: string; markers: DecodedKeyMarker[] } | null {
  let node: Element | null = start;
  let depth = 0;
  while (node && depth < MAX_WALK_DEPTH) {
    for (const attr of INSPECT_ATTRS) {
      const value = node.getAttribute(attr);
      if (value && containsKeyMarker(value)) {
        return { element: node, text: value, markers: decodeKeyMarkers(value) };
      }
    }
    const text = ownText(node);
    if (containsKeyMarker(text)) {
      return { element: node, text, markers: decodeKeyMarkers(text) };
    }
    node = node.parentElement;
    depth += 1;
  }
  return null;
}

function clearHover(): void {
  if (hoverClearTimer) {
    clearTimeout(hoverClearTimer);
    hoverClearTimer = undefined;
  }
  _hovered.value = null;
}

function scheduleHoverClear(): void {
  if (hoverClearTimer) return;
  hoverClearTimer = setTimeout(() => {
    hoverClearTimer = undefined;
    _hovered.value = null;
  }, HOVER_CLEAR_DELAY_MS);
}

function setHovered(found: { element: Element; text: string; markers: DecodedKeyMarker[] }): void {
  if (hoverClearTimer) {
    clearTimeout(hoverClearTimer);
    hoverClearTimer = undefined;
  }
  // Re-entering the same element (e.g. the pointer wiggling within it)
  // shouldn't re-measure — avoids rect churn feeding back into the overlay.
  if (_hovered.value?.element === found.element) return;
  _hovered.value = {
    element: found.element,
    text: found.text,
    markers: found.markers,
    rect: found.element.getBoundingClientRect(),
  };
}

function sameRect(a: DOMRect, b: DOMRect): boolean {
  return a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height;
}

/** Keeps the outline/icon aligned with their element across scrolling and layout changes. */
function remeasureHovered(): void {
  const current = _hovered.value;
  if (!current) return;
  if (!current.element.isConnected) {
    // Vue re-rendered this element away (e.g. a dialog that owned it closed).
    clearHover();
    return;
  }
  const rect = current.element.getBoundingClientRect();
  // Most re-measures (every transition anywhere in the app, every scroll
  // tick) find nothing moved — skip those rather than re-rendering the overlay.
  if (sameRect(rect, current.rect)) return;
  _hovered.value = { ...current, rect };
}

/** requestAnimationFrame handle for a pending {@link remeasureHovered} after a transition/animation ends. */
let remeasureFrame: ReturnType<typeof requestAnimationFrame> | undefined;

/**
 * Toasts and dialogs scale in: text hovered before that finishes (e.g. a
 * toast appearing under a pointer already resting there) is first measured
 * at a fraction of its final size and position. And when a stacked toast
 * finishes its leave animation, Vue only removes it — letting the toasts
 * below slide up — from its own handler on that element, after this
 * capture-phase listener has already run. So the re-measure waits a frame,
 * which also coalesces the burst of `transitionend`s (one per property) into
 * one pass.
 */
function handleTransitionOrAnimationEnd(): void {
  if (remeasureFrame !== undefined) return;
  remeasureFrame = requestAnimationFrame(() => {
    remeasureFrame = undefined;
    remeasureHovered();
  });
}

function handleDocumentMouseOver(event: MouseEvent): void {
  if (!_isActive.value) return;
  const target = event.target as Element | null;
  if (!target) return;
  // Moving from the outlined text onto the inspector's own hover icon must
  // not clear the hover it's anchored to — see KEY_INSPECTOR_IGNORE_ATTR.
  if (target.closest(`[${KEY_INSPECTOR_IGNORE_ATTR}]`)) return;

  pendingHoverTarget = target;
  if (hoverFrame !== undefined) return;
  hoverFrame = requestAnimationFrame(() => {
    hoverFrame = undefined;
    const current = pendingHoverTarget;
    pendingHoverTarget = null;
    if (!current) return;

    const found = findMarkerSourceForHover(current);
    if (found) {
      setHovered(found);
    } else {
      scheduleHoverClear();
    }
  });
}

function handleViewportChange(): void {
  remeasureHovered();
}

/**
 * Promotes the current hover into an open selection. Called directly by the
 * hover icon's own `@click` in {@link KeyInspectorOverlay} — not routed
 * through {@link handleDocumentClick}, which that icon is exempt from via
 * `KEY_INSPECTOR_IGNORE_ATTR`.
 */
function inspect(event: MouseEvent): void {
  const found = _hovered.value;
  if (!found || found.markers.length === 0) return;
  _selected.value = selectionFromMarkerMatch(found, { x: event.clientX, y: event.clientY });
}

// ── Click handling (Alt+click) ───────────────────────────────────────────────

/**
 * Elements carrying this attribute (and their descendants) are the inspector's
 * own UI — the floating pill, the hover outline/icon, and the popover — not
 * app content. Without this exemption, the hover/click listeners below would
 * treat the inspector's own controls the same as any other text, which makes
 * the inspector un-closable and un-usable while it's active. See
 * {@link KeyInspectorOverlay}.
 */
export const KEY_INSPECTOR_IGNORE_ATTR = 'data-key-inspector-ignore';

/**
 * Capture-phase so this runs before the element's own click handler (a
 * button's `@click`, a link's navigation) — but unlike the first version of
 * this tool, only Alt+click is intercepted; a plain click always passes
 * through untouched, which is what lets the rest of the app keep working
 * while inspect mode is on.
 */
function handleDocumentClick(event: MouseEvent): void {
  if (!_isActive.value) return;
  if (!event.altKey) return;
  const target = event.target as Element | null;
  if (!target) return;
  if (target.closest(`[${KEY_INSPECTOR_IGNORE_ATTR}]`)) return;

  event.preventDefault();
  event.stopPropagation();

  const position = { x: event.clientX, y: event.clientY };

  const found = findMarkerSource(target);
  if (found && found.markers.length > 0) {
    _selected.value = selectionFromMarkerMatch(found, position);
    return;
  }

  const text = (target.textContent ?? '').trim();
  const candidates = text ? reverseLookupKeys(text) : [];
  _selected.value = {
    key: candidates[0] ?? null,
    matchedText: text,
    template: candidates[0] ? lookupTemplate(candidates[0], undefined) : undefined,
    otherMatches: candidates.slice(1).map((key) => ({ key })),
    resolvedVia: candidates.length > 0 ? 'fallback' : 'none',
    position,
  };
}

/** Any open dialog — PrimeVue's Dialog/ConfirmDialog and the app's own modals only render these while open. */
const OPEN_DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"]';

/**
 * Escape closes the innermost thing open, one at a time: the popover first
 * (here), then whatever the app has open (left alone), and only then
 * inspect mode itself ({@link handleDocumentKeydown}). Escapes pressed
 * mid-composition in an IME are left to the IME, as PrimeVue's Dialog does.
 *
 * Capture-phase on window, not document: window hears a key before
 * anything on document does, so stopping the Escape here keeps it from
 * every document listener — a modal dialog (PrimeVue's Dialog) closing
 * itself from its bubble-phase listener, and a date-time picker
 * (AppDatePicker with show-time) cancelling from its own capture-phase one.
 * On document itself, stopPropagation() wouldn't stop that picker's
 * listener, which sits at the same spot; either way one keypress would
 * close both, throwing away whatever was typed or picked underneath.
 *
 * An Escape typed into the popover's own text field is left to that field:
 * it discards the draft (and stops propagating) itself, rather than closing
 * the whole popover.
 */
function handleWindowKeydownCapture(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || event.isComposing || !_selected.value) return;
  const target = event.target as Element | null;
  if (target?.closest?.(`[${KEY_INSPECTOR_IGNORE_ATTR}]`) && target.matches('textarea, input')) return;
  event.stopPropagation();
  _selected.value = null;
}

/**
 * Bubble-phase — unlike {@link handleWindowKeydownCapture} — so it never
 * sees an Escape a dropdown (PrimeVue Select/MultiSelect) or a date-time
 * picker already used to close itself and stopped, nor one that closed the
 * popover. While a dialog is open, the Escape is the
 * dialog's to close itself with; inspect mode only exits once nothing else
 * is open.
 */
function handleDocumentKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || event.isComposing) return;
  if (document.querySelector(OPEN_DIALOG_SELECTOR)) return;
  setActive(false);
}

/**
 * Strips markers out of whatever the user just copied, so pasting the
 * clicked-on text elsewhere (a support ticket, a chat message) doesn't carry
 * invisible characters along with it.
 *
 * Only touches the clipboard when the selection actually contains a marker —
 * leaving every other copy (code, numbers, text never routed through `t()`)
 * to the browser's default handling untouched.
 */
function handleDocumentCopy(event: ClipboardEvent): void {
  if (!_isActive.value) return;
  const selection = window.getSelection?.()?.toString() ?? '';
  if (!selection || !containsKeyMarker(selection)) return;

  event.preventDefault();
  event.clipboardData?.setData('text/plain', stripKeyMarkers(selection));
}

// ── Activation ────────────────────────────────────────────────────────────────

function attachListeners(): void {
  document.addEventListener('click', handleDocumentClick, { capture: true });
  window.addEventListener('keydown', handleWindowKeydownCapture, { capture: true });
  document.addEventListener('keydown', handleDocumentKeydown);
  document.addEventListener('copy', handleDocumentCopy);
  document.addEventListener('mouseover', handleDocumentMouseOver);
  // capture: true — scrolling inside a nested panel (a dialog body, a table)
  // doesn't bubble a 'scroll' event to window, only capture phase sees it.
  window.addEventListener('scroll', handleViewportChange, { capture: true, passive: true });
  window.addEventListener('resize', handleViewportChange);
  document.addEventListener('transitionend', handleTransitionOrAnimationEnd, { capture: true });
  document.addEventListener('animationend', handleTransitionOrAnimationEnd, { capture: true });
}

function detachListeners(): void {
  document.removeEventListener('click', handleDocumentClick, { capture: true });
  window.removeEventListener('keydown', handleWindowKeydownCapture, { capture: true });
  document.removeEventListener('keydown', handleDocumentKeydown);
  document.removeEventListener('copy', handleDocumentCopy);
  document.removeEventListener('mouseover', handleDocumentMouseOver);
  window.removeEventListener('scroll', handleViewportChange, { capture: true });
  window.removeEventListener('resize', handleViewportChange);
  document.removeEventListener('transitionend', handleTransitionOrAnimationEnd, { capture: true });
  document.removeEventListener('animationend', handleTransitionOrAnimationEnd, { capture: true });

  if (hoverFrame !== undefined) {
    cancelAnimationFrame(hoverFrame);
    hoverFrame = undefined;
  }
  if (remeasureFrame !== undefined) {
    cancelAnimationFrame(remeasureFrame);
    remeasureFrame = undefined;
  }
  pendingHoverTarget = null;
  clearHover();
}

function setActive(on: boolean): void {
  if (on === _isActive.value) return;
  _isActive.value = on;
  setKeyMarkersEnabled(on);
  if (on) {
    attachListeners();
  } else {
    detachListeners();
    _selected.value = null;
  }
}

/**
 * Wires the one-time, app-lifetime watcher that forces inspect mode off the
 * moment `isAllowed` goes false — a role change, or logout via
 * `entitlementsStore.reset()`/`authStore.clearAuth()`. Guarded by
 * {@link _initialized} so calling {@link useKeyInspector} from more than one
 * component (the menu item and the overlay both do) only installs it once.
 *
 * `flush: 'sync'` deliberately skips Vue's default microtask-batched timing:
 * this turns off marker injection, so any delay is a window where content
 * rendered synchronously right after a role change or logout could still
 * come out marked, even though `isAllowed` already reads false.
 */
function ensureAutoOffWatcher(isAllowed: Ref<boolean>): void {
  if (_initialized) return;
  _initialized = true;
  watch(
    isAllowed,
    (allowed) => {
      if (!allowed) setActive(false);
    },
    { flush: 'sync' },
  );
}

// ── Public API ────────────────────────────────────────────────────────────────

export function useKeyInspector() {
  const authStore = useAuthStore();
  const entitlementsStore = useEntitlementsStore();

  /**
   * Same rule as the Localization page and the onboarding localization step
   * (`requiredEntitlement: 'SETTINGS'` on their routes) — not a hardcoded
   * role list. `entitlementsStore.hasEntitlement` returns `true` for any key
   * until `isLoaded`, so that's checked explicitly rather than trusting the
   * default-allow behaviour meant for "not yet fetched", not "logged out".
   */
  const isAllowed = computed(
    () => authStore.isAuthenticated && entitlementsStore.isLoaded && entitlementsStore.hasEntitlement('SETTINGS'),
  );

  ensureAutoOffWatcher(isAllowed);

  function toggle(): void {
    if (!isAllowed.value) return;
    setActive(!_isActive.value);
  }

  function exit(): void {
    setActive(false);
  }

  function clearSelection(): void {
    _selected.value = null;
  }

  return {
    isAllowed,
    isActive: readonly(_isActive),
    selected: readonly(_selected),
    hovered: readonly(_hovered),
    toggle,
    exit,
    clearSelection,
    inspect,
  };
}
